import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import FeatureCarousel from './FeatureCarousel';
import FeatureSections from './FeatureSections';
import FAQSection from './FAQSection';
import Footer from './Footer';
import placeholder from '../images/placeholder.png';

const QRDemoPage: React.FC = () => {
  const { t } = useTranslation();
  const [storeUrl, setStoreUrl] = useState('https://notely.se');

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/android/.test(userAgent)) {
      setStoreUrl('https://play.google.com/store/apps/details?id=com.cstate.notelyapp');
    } else if (/iphone|ipad|ipod|macintosh/.test(userAgent)) {
      setStoreUrl('https://apps.apple.com/se/app/notely-ai/id6740462619?l=en-GB&platform=iphone');
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Floating QR Code */}
      <div className="fixed bottom-8 right-8 z-50 transition-all duration-300 ease-in-out hover:scale-105">
        <div className="bg-gray-800 p-4 sm:p-6 rounded-lg shadow-2xl border border-gray-700">
          <div className="bg-white p-2 sm:p-3 rounded-lg mb-2 sm:mb-3">
            <div className="w-[min(25vw,200px)] h-[min(25vw,200px)] sm:w-[min(20vw,250px)] sm:h-[min(20vw,250px)] md:w-[min(15vw,300px)] md:h-[min(15vw,300px)]">
              <QRCodeSVG
                value={storeUrl}
                size={300}
                level="H"
                includeMargin={true}
                className="w-full h-full"
              />
            </div>
          </div>
          <p className="text-sm sm:text-base text-gray-400 text-center">{t('qr.scanToDownload')}</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4">
        <section className="pt-16 text-center">
          <h1 className="text-4xl font-bold mb-8 animate-slide-up opacity-0" style={{ animationDelay: '0.2s' }}>
            {t('qrDemo.heroTitle')}
          </h1>
          
          {/* Add schema.org structured data */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              "name": "Notely AI",
              "applicationCategory": "ProductivityApplication",
              "operatingSystem": "iOS, Android",
              "description": "Transform your audio into perfect notes with AI-powered transcription and insights.",
            })}
          </script>

          {/* Helper function to determine the store URL */}
          {(() => {
            const isAndroid = /Android/.test(navigator.userAgent);
            const storeUrl = isAndroid 
              ? "https://play.google.com/store/apps/details?id=com.cstate.notelyapp"
              : "https://apps.apple.com/se/app/notely-ai/id6740462619?l=en-GB&platform=iphone";
            
            return (
              <a 
                href={storeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-primary text-white px-6 py-3 rounded-lg font-medium mt-4 animate-slide-up opacity-0"
                style={{ animationDelay: '0.4s' }}
              >
                {t('qrDemo.downloadButton')}
              </a>
            );
          })()}
          
          <div className="mt-12 relative w-screen md:w-full -mx-4 md:mx-auto h-[600px] md:h-[800px]">
            <div 
              className="h-full animate-slide-up opacity-0 md:p-4 md:max-w-[1200px] md:mx-auto" 
              style={{ animationDelay: '0.6s' }}
            >
              <img 
                src={placeholder} 
                alt="App screenshot"
                className="w-full h-full object-cover rounded-none md:rounded-2xl" 
              />
            </div>
          </div>
        </section>

        <section className="pb-16 mt-16">
          <div className="flex flex-col md:flex-row justify-center md:space-x-8 space-y-8 md:space-y-0 mb-8 items-center">
            <h1 className="text-3xl md:text-4xl font-bold text-center md:text-left animate-slide-up opacity-0" style={{ animationDelay: '0.8s' }}>
              {t('qrDemo.tagline')}
            </h1>
            <div className="flex gap-8 md:gap-12 animate-slide-up opacity-0" style={{ animationDelay: '1s' }}>
              <div className="text-center">
                <div className="text-primary text-4xl md:text-5xl font-bold">50m+</div>
                <div className="text-gray-400 mt-2">{t('qrDemo.minutesOfAudio')}</div>
              </div>
              <div className="text-center">
                <div className="text-primary text-4xl md:text-5xl font-bold">4.9</div>
                <div className="text-gray-400 mt-2">{t('qrDemo.ratingOutOf5')}</div>
              </div>
            </div>
          </div>
        </section>

        <FeatureCarousel />
        <FeatureSections />
        <FAQSection />
        <Footer />
      </div>
    </div>
  );
};

export default QRDemoPage; 